import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileSearch, ExternalLink, Calendar, Building } from "lucide-react";

export default function Tenders() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenders");
        setTenders(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="tenders-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileSearch className="h-7 w-7 text-cyan-400" /> Moniteur d'appels d'offres
        </h1>
        <p className="text-muted-foreground mt-1">Suivi des appels d'offres publics pertinents pour votre secteur.</p>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-center py-12">Chargement…</div>
      ) : tenders.length === 0 ? (
        <Card className="p-12 text-center border-dashed">Aucun appel d'offres pour le moment.</Card>
      ) : (
        <div className="space-y-3">
          {tenders.map((t) => (
            <Card key={t.tender_id} className="p-5 bg-card border-border card-hover" data-testid={`tender-${t.tender_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {t.closing_soon && <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30">FERMETURE PROCHE</Badge>}
                    <Badge variant="outline" className="text-[10px]">{t.reference_number}</Badge>
                    {t.is_demo && <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[10px]">DÉMO</Badge>}
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-1">{t.title}</h3>
                  <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-3 mb-3">
                    <span className="flex items-center gap-1"><Building className="h-3 w-3" /> {t.organization}</span>
                    <span>·</span>
                    <span>{t.location}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Ferme le {new Date(t.closing_date).toLocaleDateString("fr-CA")}</span>
                    {t.days_to_close != null && <span className={t.closing_soon ? "text-amber-400 font-medium" : ""}>({t.days_to_close} j)</span>}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{t.description}</p>
                </div>
                <div className="text-right shrink-0">
                  {t.estimated_value && (
                    <div className="text-cyan-300 font-display font-bold mb-2">
                      {new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(t.estimated_value)}
                    </div>
                  )}
                  {t.source_url && (
                    <a href={t.source_url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline text-xs flex items-center gap-1 justify-end">
                      Source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
